import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope, faArrowLeft, faPlus, faTrash, faSpinner,
  faCheckCircle, faTimesCircle, faSave, faLink, faPlug, faFlask,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';
import MainLayout from '../Layout/MainLayout';

const blankCategory = () => ({
  display_name: '',
  ai_label: '',
  description: '',
  sample_subjects: '',
  sender_contains: '',
  require_attachment: true,
  target_workflow_name: '',
  enabled: true,
  priority: 100,
});

const EmailRouterForm = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = !!id;

  const [config, setConfig] = useState({
    name: '',
    inbox_email: '',
    inbox_folder: 'Inbox',
    poll_interval_minutes: 5,
    fallback_action: 'skip',
    fallback_notify_email: '',
    enabled: true,
    auth_mode: 'sso',
    tenant_id: '',
    client_id: '',
    client_secret: '',
    categories: [blankCategory()],
  });

  const [availableWorkflows, setAvailableWorkflows] = useState([]);
  const [credentialsStatus, setCredentialsStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchWorkflows();
    if (isEdit) fetchConfig();
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (searchParams.get('oauth_success')) {
      setTestResult({ ok: true, msg: 'Microsoft account connected successfully.' });
    } else if (searchParams.get('oauth_error')) {
      setTestResult({ ok: false, msg: `OAuth failed: ${searchParams.get('oauth_error')}` });
    }
  }, [searchParams]);

  const fetchWorkflows = async () => {
    try {
      const resp = await fetch('/api/email-router/available-workflows', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      setAvailableWorkflows(data.workflows || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const resp = await fetch(`/api/email-router/configs/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Could not load config');
      const data = await resp.json();
      const c = data.config;
      const cats = (data.categories || []).map((cat) => ({
        ...blankCategory(),
        ...cat,
        require_attachment: !!cat.require_attachment,
        enabled: !!cat.enabled,
      }));
      const cs = data.credentials_status || {};
      setCredentialsStatus(cs);
      setConfig({
        name: c.name,
        inbox_email: c.inbox_email,
        inbox_folder: c.inbox_folder || 'Inbox',
        poll_interval_minutes: c.poll_interval_minutes || 5,
        fallback_action: c.fallback_action || 'skip',
        fallback_notify_email: c.fallback_notify_email || '',
        enabled: !!c.enabled,
        auth_mode: cs.auth_mode || 'sso',
        // Show masked values so it's clear credentials are saved. The • sentinel
        // tells the backend "unchanged" so it keeps the stored value on save.
        tenant_id: cs.tenant_id_masked || '',
        client_id: cs.client_id_masked || '',
        client_secret: cs.has_secret ? '••••••••••••' : '',
        categories: cats.length ? cats : [blankCategory()],
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const update = (field, value) => setConfig((c) => ({ ...c, [field]: value }));

  const updateCategory = (idx, field, value) => {
    setConfig((c) => {
      const cats = [...c.categories];
      cats[idx] = { ...cats[idx], [field]: value };
      return { ...c, categories: cats };
    });
  };

  const addCategory = () => setConfig((c) => ({ ...c, categories: [...c.categories, blankCategory()] }));
  const removeCategory = (idx) => setConfig((c) => ({ ...c, categories: c.categories.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // A rule the user has started filling (any field touched).
      const startedRules = config.categories.filter(
        (c) => c.ai_label || c.target_workflow_name || c.display_name || c.description || c.sample_subjects || c.sender_contains
      );
      // Each started rule MUST have both an AI label and a target workflow.
      const incomplete = startedRules.filter((c) => !c.ai_label || !c.target_workflow_name);
      if (incomplete.length > 0) {
        setError('Each routing rule needs both an "AI label" and a "Target agent (workflow)". Please complete or remove the incomplete rule(s) before saving.');
        setSaving(false);
        return;
      }
      // Default display_name to the AI label if left blank.
      const cleanedCategories = startedRules.map((c) => ({
        ...c,
        display_name: c.display_name || c.ai_label,
      }));
      // Coerce poll interval to a valid integer >= 1 (the field may be left blank
      // or mid-edit); fall back to 5 if empty/invalid.
      const pollInterval = Math.max(1, parseInt(config.poll_interval_minutes, 10) || 5);
      const payload = { ...config, poll_interval_minutes: pollInterval, categories: cleanedCategories };

      const url = isEdit ? `/api/email-router/configs/${id}` : '/api/email-router/configs';
      const method = isEdit ? 'PUT' : 'POST';
      const resp = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || 'Save failed');
      }
      const data = await resp.json();
      if (!isEdit && data.config_id) {
        navigate(`/email-router/edit/${data.config_id}`);
      } else {
        navigate('/email-router');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleConnectMicrosoft = async () => {
    if (!id) {
      setError('Save the inbox first, then click Connect.');
      return;
    }
    try {
      const resp = await fetch(`/api/email-router/oauth/start?config_id=${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (data.authorize_url) {
        window.location.href = data.authorize_url;
      } else {
        setError(data.detail || 'Could not start OAuth');
      }
    } catch (e) {
      setError(e.message);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const resp = await fetch('/api/email-router/test-connection', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          auth_mode: config.auth_mode,
          inbox_email: config.inbox_email,
          tenant_id: config.tenant_id,
          client_id: config.client_id,
          client_secret: config.client_secret,
          config_id: id ? parseInt(id, 10) : null,
        }),
      });
      const data = await resp.json();
      setTestResult({ ok: data.success, msg: data.success ? 'Connection OK' : (data.error || 'Failed') });
    } catch (e) {
      setTestResult({ ok: false, msg: e.message });
    } finally {
      setTesting(false);
    }
  };

  return (
    <MainLayout>
      <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>
        <button onClick={() => navigate('/email-router')} style={backBtn}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back
        </button>

        <h1 style={{ marginTop: 8 }}>
          <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: 12, color: '#5b8def' }} />
          {isEdit ? 'Edit Inbox' : 'Add Inbox'}
        </h1>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <FontAwesomeIcon icon={faSpinner} spin /> Loading…
          </div>
        ) : (
          <>
            {error && (
              <div style={errorBox}><FontAwesomeIcon icon={faTimesCircle} /> {error}</div>
            )}

            {/* ---- Inbox details ---- */}
            <section style={card}>
              <h3 style={sectionTitle}>Inbox Details</h3>
              <Row label="Name">
                <input value={config.name} onChange={(e) => update('name', e.target.value)} style={input} placeholder="AP Inbox" />
              </Row>
              <Row label="Email address">
                <input value={config.inbox_email} onChange={(e) => update('inbox_email', e.target.value)} style={input} placeholder="accounts@bytebridge.com" />
              </Row>
              <Row label="Folder">
                <input value={config.inbox_folder} onChange={(e) => update('inbox_folder', e.target.value)} style={input} placeholder="Inbox" />
              </Row>
              <Row label="Poll interval (min)">
                <input type="number" min={1} value={config.poll_interval_minutes} onChange={(e) => update('poll_interval_minutes', e.target.value === '' ? '' : parseInt(e.target.value, 10))} style={{ ...input, width: 100 }} />
              </Row>
              <Row label="Enabled">
                <input type="checkbox" checked={config.enabled} onChange={(e) => update('enabled', e.target.checked)} />
              </Row>
            </section>

            {/* ---- Authentication ---- */}
            <section style={card}>
              <h3 style={sectionTitle}>Authentication</h3>
              <Row label="Method">
                <label style={{ marginRight: 18 }}>
                  <input type="radio" checked={config.auth_mode === 'sso'} onChange={() => update('auth_mode', 'sso')} /> Sign in with Microsoft (SSO)
                </label>
                <label>
                  <input type="radio" checked={config.auth_mode === 'app_credentials'} onChange={() => update('auth_mode', 'app_credentials')} /> Use App Credentials
                </label>
              </Row>

              {config.auth_mode === 'sso' && (
                <div style={{ marginTop: 12 }}>
                  {credentialsStatus?.connected && credentialsStatus?.auth_mode === 'sso' ? (
                    <div style={connectedBox}>
                      <FontAwesomeIcon icon={faCheckCircle} /> Connected via Microsoft. Token refreshes automatically.
                    </div>
                  ) : (
                    <div>
                      <p style={{ color: '#666', fontSize: 13 }}>
                        Save the inbox first, then click below to sign in. We'll get permission to read the configured mailbox only.
                      </p>
                      <button onClick={handleConnectMicrosoft} style={btnPrimary} disabled={!isEdit}>
                        <FontAwesomeIcon icon={faLink} /> Connect with Microsoft
                      </button>
                      {!isEdit && <span style={{ marginLeft: 12, color: '#888', fontSize: 13 }}>(save first)</span>}
                    </div>
                  )}
                </div>
              )}

              {config.auth_mode === 'app_credentials' && (
                <div style={{ marginTop: 12 }}>
                  <Row label="Tenant ID">
                    <input value={config.tenant_id} onChange={(e) => update('tenant_id', e.target.value)} style={input} placeholder="11111111-2222-3333-4444-555555555555" />
                  </Row>
                  <Row label="Client ID">
                    <input value={config.client_id} onChange={(e) => update('client_id', e.target.value)} style={input} placeholder="application (client) id" />
                  </Row>
                  <Row label="Client Secret">
                    <input type="password" value={config.client_secret} onChange={(e) => update('client_secret', e.target.value)} style={input} placeholder={isEdit && credentialsStatus?.connected ? '••••• (leave blank to keep)' : 'app secret value'} />
                  </Row>
                  <button onClick={handleTestConnection} style={btnSecondary} disabled={testing}>
                    {testing ? <><FontAwesomeIcon icon={faSpinner} spin /> Testing…</> : <><FontAwesomeIcon icon={faPlug} /> Test Connection</>}
                  </button>
                </div>
              )}

              {testResult && (
                <div style={{ marginTop: 12, ...(testResult.ok ? connectedBox : errorBox) }}>
                  <FontAwesomeIcon icon={testResult.ok ? faCheckCircle : faTimesCircle} /> {testResult.msg}
                </div>
              )}
            </section>

            {/* ---- Routing rules ---- */}
            <section style={card}>
              <h3 style={sectionTitle}>Routing Rules</h3>
              <p style={{ color: '#666', fontSize: 13 }}>
                For each email type, pick which agent workflow to trigger. AI matches the email against these rules.
              </p>
              {config.categories.map((cat, idx) => (
                <div key={idx} style={categoryCard}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <b>Rule {idx + 1}</b>
                    {config.categories.length > 1 && (
                      <button onClick={() => removeCategory(idx)} style={removeBtn}>
                        <FontAwesomeIcon icon={faTrash} /> Remove
                      </button>
                    )}
                  </div>

                  <Row label="Display name">
                    <input value={cat.display_name} onChange={(e) => updateCategory(idx, 'display_name', e.target.value)} style={input} placeholder="Vendor Bills" />
                  </Row>
                  <Row label="AI label (short)">
                    <input value={cat.ai_label} onChange={(e) => updateCategory(idx, 'ai_label', e.target.value)} style={input} placeholder="bill" />
                  </Row>
                  <Row label="Target agent (workflow)">
                    <select value={cat.target_workflow_name} onChange={(e) => updateCategory(idx, 'target_workflow_name', e.target.value)} style={input}>
                      <option value="">— select —</option>
                      {availableWorkflows.map((w) => (
                        <option key={w.id} value={w.name}>
                          {w.agent_name ? `${w.agent_name} → ${w.name}` : w.name}
                        </option>
                      ))}
                    </select>
                  </Row>
                  <Row label="Description (helps AI)">
                    <textarea value={cat.description || ''} onChange={(e) => updateCategory(idx, 'description', e.target.value)} style={{ ...input, height: 56 }} placeholder="Vendor invoices, bills, payment requests…" />
                  </Row>
                  <Row label="Sample subjects (one per line)">
                    <textarea value={cat.sample_subjects || ''} onChange={(e) => updateCategory(idx, 'sample_subjects', e.target.value)} style={{ ...input, height: 56 }} placeholder={'Invoice #\nBill from ...'} />
                  </Row>
                  <Row label="Sender contains (optional)">
                    <input value={cat.sender_contains || ''} onChange={(e) => updateCategory(idx, 'sender_contains', e.target.value)} style={input} placeholder="vendor@" />
                  </Row>
                  <Row label="Require attachment">
                    <input type="checkbox" checked={cat.require_attachment} onChange={(e) => updateCategory(idx, 'require_attachment', e.target.checked)} />
                  </Row>
                  <Row label="Enabled">
                    <input type="checkbox" checked={cat.enabled} onChange={(e) => updateCategory(idx, 'enabled', e.target.checked)} />
                  </Row>
                </div>
              ))}

              <button onClick={addCategory} style={btnSecondary}>
                <FontAwesomeIcon icon={faPlus} /> Add Rule
              </button>
            </section>

            {/* ---- Save / Cancel ---- */}
            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <button onClick={handleSave} disabled={saving} style={btnPrimary}>
                {saving ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving…</> : <><FontAwesomeIcon icon={faSave} /> Save</>}
              </button>
              <button onClick={() => navigate('/email-router')} style={btnSecondary}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
};

const Row = ({ label, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
    <label style={{ width: 220, color: '#555', fontSize: 14 }}>{label}</label>
    <div style={{ flex: 1 }}>{children}</div>
  </div>
);

// --- styles ---
const card = {
  background: 'white', borderRadius: 8, padding: 20, marginTop: 16,
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};
const sectionTitle = { marginTop: 0, marginBottom: 16, color: '#333' };
const input = {
  padding: '8px 10px', border: '1px solid #d9dde3', borderRadius: 5,
  fontSize: 14, width: '100%', boxSizing: 'border-box',
};
const btnPrimary = {
  background: '#5b8def', color: 'white', border: 'none', padding: '10px 18px',
  borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600,
};
const btnSecondary = {
  background: 'white', color: '#5b8def', border: '1px solid #5b8def',
  padding: '10px 18px', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600,
};
const backBtn = {
  background: 'transparent', border: 'none', color: '#5b8def',
  cursor: 'pointer', fontSize: 14, padding: 0,
};
const categoryCard = {
  background: '#f7f9fc', borderRadius: 6, padding: 16, marginBottom: 12,
};
const removeBtn = {
  background: 'transparent', border: '1px solid #c0392b', color: '#c0392b',
  padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12,
};
const errorBox = {
  background: '#fdecec', color: '#c0392b', padding: 12, borderRadius: 6, marginBottom: 12,
};
const connectedBox = {
  background: '#e8f5ee', color: '#1f8a3a', padding: 12, borderRadius: 6,
};

export default EmailRouterForm;
